import { useTranslation } from 'react-i18next';

import type { IntervalRow } from '@/pages/anatel/conselho/IntervalList';
import type {
  ConselhoEvidence,
  ConselhoMetric,
  ConselhoOrdering,
  ConselhoReading,
  ConselhoScopeTally,
} from '@/services/types';

export function metricRows(rows: ConselhoMetric[]): IntervalRow[] {
  return rows.map((r) => ({
    key: `${r.name}|${r.scope}`,
    name: r.scope === 'overall' ? r.name : `${r.name} — ${r.scope}`,
    slug: r.slug,
    n: r.n,
    raw: r.raw_rate,
    effect: r.adjusted_effect,
    lower: r.lower,
    upper: r.upper,
    suppressed: r.suppressed,
  }));
}

export function EvidenceCard({ evidence, ns = 'anatel' }: { evidence: ConselhoEvidence; ns?: string }) {
  const { t, i18n } = useTranslation([ns]);
  const number = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 3 });
  return (
    <section
      className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-100"
      data-evidence-level={evidence.level}
    >
      <p className="font-semibold">{t(`${ns}:conselho.evidence.title`)}</p>
      <p>{t(`${ns}:conselho.evidence.${evidence.level}`, { lift: number.format(evidence.pooled_lift ?? 0) })}</p>
      {evidence.tests > 0 ? (
        <p className="text-[11px]">
          {t(`${ns}:conselho.evidence.tests`, { significant: evidence.significant_tests, tests: evidence.tests })}
          {evidence.bonferroni_significant != null ? (
            <span data-testid="evidence-bonferroni">
              {'; '}
              {t(`${ns}:conselho.evidence.bonferroni`, { count: evidence.bonferroni_significant })}
            </span>
          ) : null}
        </p>
      ) : null}
      {evidence.year_imbalanced ? (
        <p className="text-[11px]" data-testid="evidence-year">
          {t(`${ns}:conselho.evidence.yearImbalanced`)}
        </p>
      ) : null}
    </section>
  );
}

/**
 * Why a bench is listed by name. Rendered ABOVE the list, so the reader meets
 * the reason before the names; renders nothing when the record supports an
 * order (the list is then by effect, as its axis says).
 */
export function OrderNote({ order }: { order: ConselhoOrdering | null | undefined }) {
  const { t, i18n } = useTranslation(['anatel']);
  if (!order || order.ranked || !order.reason) return null;
  const rho = new Intl.NumberFormat(i18n.language, { signDisplay: 'exceptZero', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <p className="mb-2 text-[11px] text-muted-foreground" data-testid="order-note" data-reason={order.reason}>
      {t(`anatel:conselho.order.${order.reason}`, {
        listed: order.listed,
        excluding: order.excluding_zero,
        rho: order.era_rho != null ? rho.format(order.era_rho) : '—',
        n: order.era_n ?? 0,
      })}
    </p>
  );
}

/**
 * What the severity run's own validation says about reading an effect as a
 * property of the person. Every value is interpolated from the run, so the
 * sentence cannot keep describing a check that has since changed.
 */
export function ReadingNote({ reading }: { reading: ConselhoReading | null | undefined }) {
  const { t, i18n } = useTranslation(['anatel']);
  if (!reading) return null;
  const two = new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const signed2 = new Intl.NumberFormat(i18n.language, { signDisplay: 'exceptZero', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const signed3 = new Intl.NumberFormat(i18n.language, { signDisplay: 'exceptZero', minimumFractionDigits: 3, maximumFractionDigits: 3 });
  const fmt = (f: Intl.NumberFormat, v: number | null) => (v === null ? '—' : f.format(v));
  const key = reading.stable === null ? 'unknown' : reading.stable ? 'stable' : 'unstable';
  return (
    <div
      className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-100"
      data-testid="reading-note"
      data-stable={String(reading.stable)}
    >
      <p className="font-semibold">{t('anatel:conselho.reading.title')}</p>
      <p>
        {t(`anatel:conselho.reading.${key}`, {
          placebo: fmt(two, reading.placebo_p),
          split: fmt(signed2, reading.split_r),
          splitN: reading.split_n,
          auc: fmt(signed3, reading.auc_lift),
        })}
      </p>
      {reading.sensitivity_rho !== null ? (
        <p className="text-[11px]">
          {t('anatel:conselho.reading.sensitivity', { rho: two.format(reading.sensitivity_rho), n: reading.sensitivity_n })}
        </p>
      ) : null}
    </div>
  );
}

/** One member's scoped rows excluding zero, beside what chance alone gives at 90%. */
export function TallyNote({ tally }: { tally: ConselhoScopeTally | null | undefined }) {
  const { t } = useTranslation(['anatel']);
  if (!tally || tally.listed === 0) return null;
  return (
    <p className="mt-1 text-[11px] text-muted-foreground" data-testid="tally-note">
      {t('anatel:conselheiro.conselho.tally', {
        excluding: tally.excluding_zero,
        listed: tally.listed,
        expected: tally.expected_by_chance,
      })}
    </p>
  );
}

